import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { enviarLectura } from '../utils/api';
import { useLectura } from '../context/LecturaContext';
import AdUnit from './AdUnit';

const LS_KEY = 'tmd_lecturas';
function incrementarContadorLectura() {
  try {
    const raw  = localStorage.getItem(LS_KEY);
    const data = raw ? JSON.parse(raw) : { count: 0, resetAt: Date.now() + 24*60*60*1000 };
    const ahora = Date.now();
    if (ahora > data.resetAt) {
      localStorage.setItem(LS_KEY, JSON.stringify({ count: 1, resetAt: ahora + 24*60*60*1000 }));
    } else {
      data.count += 1;
      localStorage.setItem(LS_KEY, JSON.stringify(data));
    }
  } catch {}
}

function Procesando() {
  const navigate = useNavigate();
  const { pendingForm, resultado, guardarResultado, limpiarPendingForm } = useLectura();

  const [mensaje, setMensaje]             = useState('Selene está analizando tu mano...');
  const [progreso, setProgreso]           = useState(0);
  const [apiDone, setApiDone]             = useState(false);
  const [minTimePassed, setMinTimePassed] = useState(false);
  const [errorApi, setErrorApi]           = useState('');
  const apiDataRef = useRef(null);

  const mensajes = [
    'Selene está analizando tu mano...',
    'Leyendo línea de la vida...',
    'Calculando destino...',
    'Los astros susurran tu nombre...',
    'Detectando líneas principales...',
    'El universo prepara tu mensaje...',
    'Interpretando los elementos...',
    'Selene ve lo que otros no pueden...',
    'Preparando tu revelación...',
  ];

  useEffect(() => {
    // Sin datos pendientes: redirigir
    if (!pendingForm) {
      if (resultado) { navigate('/resultado'); return; }
      navigate('/');
      return;
    }

    // Rotar mensajes
    let idx = 0;
    const msgInterval = setInterval(() => {
      idx = (idx + 1) % mensajes.length;
      setMensaje(mensajes[idx]);
    }, 2000);

    // Animar progreso hasta 88% (esperando API)
    const progresoInterval = setInterval(() => {
      setProgreso(prev => {
        if (prev >= 88) { clearInterval(progresoInterval); return 88; }
        return prev + 1;
      });
    }, 110); // 88 pasos × 110ms ≈ 9.7s de animación

    // Tiempo mínimo de pantalla (2s para que se vea la animación)
    const minTimer = setTimeout(() => setMinTimePassed(true), 2000);

    // Llamar a la API real
    enviarLectura(pendingForm)
      .then(data => {
        incrementarContadorLectura(); // contabilizar lectura exitosa
        apiDataRef.current = data;
        setApiDone(true);
      })
      .catch(err => {
        clearInterval(msgInterval);
        clearInterval(progresoInterval);
        const errData = err.response?.data;
        if (errData?.error === 'LIMITE_ALCANZADO') {
          // Forzar localStorage al límite para que Formulario muestre el bloqueo
          try {
            localStorage.setItem(LS_KEY, JSON.stringify({ count: 2, resetAt: Date.now() + 24*60*60*1000 }));
          } catch {}
          setErrorApi(errData.mensaje || 'Has alcanzado el límite de lecturas gratuitas.');
        } else {
          setErrorApi(errData?.error || 'Selene no pudo completar la lectura. Intenta de nuevo.');
        }
      });

    return () => {
      clearInterval(msgInterval);
      clearInterval(progresoInterval);
      clearTimeout(minTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Navegar cuando API termina Y tiempo mínimo pasó
  useEffect(() => {
    if (apiDone && minTimePassed && apiDataRef.current) {
      setProgreso(100);
      setMensaje('¡Tu lectura está lista!');
      setTimeout(() => {
        guardarResultado(apiDataRef.current);
        limpiarPendingForm();
        navigate('/resultado');
      }, 600);
    }
  }, [apiDone, minTimePassed, navigate, guardarResultado, limpiarPendingForm]);

  // Pantalla de error
  if (errorApi) {
    return (
      <div className="contenedor" style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center', textAlign: 'center'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>😔</div>
        <p style={{ color: 'var(--color-acento)', marginBottom: '1.5rem', maxWidth: '360px' }}>{errorApi}</p>
        <button className="boton" onClick={() => navigate('/lectura')}>Volver al formulario</button>
      </div>
    );
  }

  return (
    <div className="contenedor" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center'
    }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{ width: '100%', maxWidth: '400px' }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          style={{ fontSize: '4rem', marginBottom: '2rem' }}
        >
          🔮
        </motion.div>

        <h2 className="titulo" style={{ fontSize: '1.5rem', marginBottom: '2rem' }}>
          {mensaje}
        </h2>

        <div style={{
          width: '100%',
          height: '4px',
          background: 'var(--color-secundario)',
          borderRadius: '2px',
          overflow: 'hidden',
          marginBottom: '1rem'
        }}>
          <motion.div
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, var(--color-acento), var(--color-oro))',
              width: `${progreso}%`
            }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <p style={{ color: 'var(--color-texto-suave)', fontSize: '0.9rem' }}>
          {progreso}% completado
        </p>

        <motion.div
          style={{ marginTop: '2rem', fontSize: '1.2rem' }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          ✋ 🌙 ✨
        </motion.div>

        {/* AD-2: mientras se genera la lectura */}
        <div style={{ marginTop: '2rem', width: '100%' }}>
          <AdUnit adKey="4061e3add7138dbcb32d917a00a78d0d" width={320} height={50} />
        </div>

      </motion.div>
    </div>
  );
}

export default Procesando;
